import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Search, Calendar, Clock, ArrowLeft, Menu, X } from "lucide-react";
import SEO from "../components/SEO";
import "../home.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = [
  { key: "all", label: "All" },
  { key: "reflections", label: "Reflections" },
  { key: "field-notes", label: "Field Notes" },
  { key: "policy", label: "Policy" },
  { key: "initiative-updates", label: "Initiative Updates" },
];
const PAGE_SIZE = 12;

export default function BlogListingPage() {
  const pageRef = useRef(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  useEffect(() => {
    const root = pageRef.current;
    if (!root) return;
    const els = root.querySelectorAll(".hm-fade");
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("visible"); obs.unobserve(e.target); } }),
      { threshold: 0.08, rootMargin: "0px 0px -40px 0px" }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [loading]);

  useEffect(() => {
    (async () => {
      try {
        await axios.post(`${API}/blog/seed`);
        const res = await axios.get(`${API}/blog`);
        setPosts(res.data);
      } catch {
        /* fetch failed silently */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [activeCategory, searchQuery]);

  const filtered = posts.filter((p) => {
    const matchCat = activeCategory === "all" || p.category === activeCategory;
    const q = searchQuery.toLowerCase();
    const matchSearch = !q || p.title.toLowerCase().includes(q) || (p.excerpt || "").toLowerCase().includes(q);
    return matchCat && matchSearch;
  });

  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  const formatDate = (post) => {
    if (post.display_date) return post.display_date;
    return new Date(post.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  };

  const readTime = (content) => {
    const words = content?.split(/\s+/).length || 0;
    return `${Math.max(1, Math.ceil(words / 200))} min`;
  };

  const catColors = {
    reflections: { bg: "var(--green-l)", color: "var(--green)" },
    "field-notes": { bg: "var(--coral-l)", color: "var(--coral)" },
    policy: { bg: "var(--blue-l)", color: "var(--blue)" },
    "initiative-updates": { bg: "var(--amber-l)", color: "var(--amber)" },
  };

  return (
    <div data-testid="blog-listing-page" ref={pageRef} style={{ fontFamily: "'DM Sans', sans-serif", color: "var(--ink)", minHeight: "100vh", background: "var(--white)" }}>
      <SEO
        title="Updates — Tulsi Giri"
        description="Reflections, field notes, policy updates, and dispatches from two decades of food systems work across Nepal and Switzerland."
        path="/blog"
      />

      {/* ═══ Nav ═══ */}
      <header className={`hm-nav ${scrolled ? "scrolled" : ""}`} data-testid="blog-nav">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between h-16">
          <Link to="/" className="hm-display text-xl tracking-tight" style={{ color: "var(--ink)", textDecoration: "none" }} data-testid="blog-nav-wordmark">
            Tulsi <span style={{ color: "var(--green)" }}>Giri</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8" data-testid="blog-nav-links">
            <Link to="/" className="hm-body text-sm font-medium transition-colors" style={{ color: "var(--ink3)", textDecoration: "none" }}>Home</Link>
            <Link to="/blog" className="hm-body text-sm font-semibold transition-colors" style={{ color: "var(--ink)", textDecoration: "none" }}>Updates</Link>
          </nav>
          <Link to="/#contact" className="hm-btn-filled hidden md:inline-flex" style={{ padding: "8px 22px", fontSize: "0.85rem", textDecoration: "none" }}>Let's Connect</Link>
          <button className="md:hidden" onClick={() => setMobileOpen(true)} aria-label="Open menu" style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ink)" }} data-testid="blog-nav-hamburger"><Menu size={24} /></button>
        </div>
      </header>

      {/* Mobile overlay */}
      <div className={`hm-overlay ${mobileOpen ? "open" : ""}`}>
        <button onClick={() => setMobileOpen(false)} aria-label="Close menu" style={{ position: "absolute", top: 20, right: 20, background: "none", border: "none", cursor: "pointer", color: "var(--ink)" }}><X size={28} /></button>
        <Link to="/" className="hm-display text-2xl" style={{ color: "var(--ink)", textDecoration: "none" }}>Home</Link>
        <Link to="/blog" onClick={() => setMobileOpen(false)} className="hm-display text-2xl" style={{ color: "var(--ink)", textDecoration: "none" }}>Updates</Link>
        <Link to="/#contact" onClick={() => setMobileOpen(false)} className="hm-btn-filled mt-4" style={{ textDecoration: "none" }}>Let's Connect</Link>
      </div>

      {/* ═══ Hero ═══ */}
      <section className="py-14 md:py-20" style={{ background: "var(--sky)" }} data-testid="blog-hero">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-2 mb-4 hm-fade">
            <Link to="/" className="hm-body text-sm" style={{ color: "var(--ink3)", textDecoration: "none" }}>Home</Link>
            <span style={{ color: "var(--div)" }}>/</span>
            <span className="hm-body text-sm" style={{ color: "var(--ink)" }}>Updates</span>
          </div>
          <h1 className="hm-display hm-fade" style={{ fontSize: "clamp(2rem, 5vw, 2.8rem)", fontWeight: 700, color: "var(--ink)", transitionDelay: "100ms" }} data-testid="blog-hero-heading">From the Field</h1>
          <p className="hm-fade mt-2" style={{ color: "var(--ink3)", fontSize: "1rem", lineHeight: 1.6, maxWidth: 520, transitionDelay: "200ms" }} data-testid="blog-hero-sub">Reflections, updates, and dispatches from two decades of building.</p>
        </div>
      </section>

      {/* ═══ Filter + Search ═══ */}
      <div className="border-b" style={{ borderColor: "var(--div)", background: "var(--white)" }} data-testid="blog-filter-bar">
        <div className="max-w-6xl mx-auto px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex flex-wrap gap-2" data-testid="blog-category-tabs">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.key}
                data-testid={`blog-tab-${cat.key}`}
                onClick={() => setActiveCategory(cat.key)}
                className={`hm-filter ${activeCategory === cat.key ? "active" : ""}`}
              >
                {cat.label}
              </button>
            ))}
          </div>
          <div className="relative" data-testid="blog-search">
            <Search size={15} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--ink3)" }} />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="blog-search-input"
              style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "0.85rem", padding: "8px 12px 8px 36px", border: "1px solid var(--div)", borderRadius: 20, outline: "none", width: 220, background: "var(--white)", color: "var(--ink)" }}
            />
          </div>
        </div>
      </div>

      {/* ═══ Article Grid ═══ */}
      <section className="py-10 md:py-14" style={{ background: "var(--white)" }} data-testid="blog-grid-section">
        <div className="max-w-6xl mx-auto px-6">
          {!loading && (
            <p className="hm-mono hm-fade mb-6" style={{ fontSize: "0.65rem", color: "var(--ink3)", letterSpacing: "0.12em", textTransform: "uppercase" }} data-testid="blog-post-count">
              Showing {visible.length} of {filtered.length} article{filtered.length !== 1 ? "s" : ""}
            </p>
          )}

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-skeletons">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} style={{ background: "var(--sky)", borderRadius: 12, height: 320, animation: "pulse 1.5s ease-in-out infinite" }} />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16" data-testid="blog-empty">
              <p className="hm-display text-xl" style={{ color: "var(--ink)" }}>No articles found</p>
              <p className="hm-body mt-2" style={{ color: "var(--ink3)" }}>
                {searchQuery ? "Try a different search term." : "Check back soon for new content."}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-articles-grid">
                {visible.map((post) => (
                  <Link
                    key={post.id}
                    to={`/blog/${post.id}`}
                    data-testid={`blog-article-${post.id}`}
                    className="hm-fade group"
                    style={{ textDecoration: "none", color: "inherit", border: "1px solid var(--div)", borderRadius: 12, overflow: "hidden", background: "var(--white)", transition: "box-shadow 300ms ease, transform 200ms ease" }}
                    onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.06)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "none"; }}
                  >
                    {/* Image */}
                    <div style={{ height: 180, overflow: "hidden", background: "var(--sky)" }}>
                      {post.image_url ? (
                        <img src={post.image_url} alt={post.title} style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 400ms ease" }} loading="lazy" />
                      ) : (
                        <div style={{ width: "100%", height: "100%", background: "linear-gradient(135deg, var(--sky), var(--div))" }} />
                      )}
                    </div>

                    {/* Body */}
                    <div style={{ padding: "20px 20px 24px" }}>
                      {post.category && (
                        <span className="hm-tag" style={{ background: catColors[post.category]?.bg || "var(--sky)", color: catColors[post.category]?.color || "var(--ink3)", marginBottom: 10, display: "inline-block" }}>
                          {post.category.replace("-", " ")}
                        </span>
                      )}
                      <h3 className="hm-display" style={{ fontSize: "1.1rem", fontWeight: 600, color: "var(--ink)", lineHeight: 1.3, marginTop: 4 }}>{post.title}</h3>
                      <p className="hm-body mt-2" style={{ fontSize: "0.85rem", color: "var(--ink3)", lineHeight: 1.6, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{post.excerpt}</p>
                      <div className="flex items-center gap-4 mt-4" style={{ fontSize: "0.75rem", color: "var(--ink3)" }}>
                        <span className="flex items-center gap-1"><Calendar size={11} /> {formatDate(post)}</span>
                        <span className="flex items-center gap-1"><Clock size={11} /> {readTime(post.content)}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {hasMore && (
                <div className="text-center mt-10">
                  <button
                    data-testid="blog-load-more"
                    className="hm-btn-ghost"
                    onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                  >
                    Load More
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* ═══ Footer ═══ */}
      <footer className="py-8" style={{ background: "var(--ink)" }} data-testid="blog-footer">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
            <p style={{ color: "rgba(255,255,255,0.25)", fontSize: "0.8rem" }}>&copy; 2026 Tulsi Giri</p>
            <div className="flex items-center gap-4" style={{ color: "rgba(255,255,255,0.25)", fontSize: "0.8rem" }}>
              <Link to="/" style={{ color: "inherit", textDecoration: "none" }}>Home</Link>
              <Link to="/blog" style={{ color: "inherit", textDecoration: "none" }}>Updates</Link>
              <Link to="/admin" style={{ color: "inherit", textDecoration: "none" }}>Admin</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
